import React, { useRef } from 'react'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import {
  marketingBrand,
  marketingColors as colors,
  PAPER,
  marketingEase as ease,
} from '../../lib/marketingBrand'

/** Illustrations in `public/illustrations` — rotate across pages so heroes don’t all match */
export const ACCENT = {
  sketchColonial: '/illustrations/house-sketch-colonial.png',
  embossColonial: '/illustrations/house-emboss-colonial.png',
  embossModern: '/illustrations/house-emboss-modern.png',
  embossEstate: '/illustrations/house-emboss-estate.png',
  penBlueprint: '/illustrations/pen-blueprint.png',
  letterpress: '/illustrations/logo-letterpress.png',
  textureWarm: '/illustrations/texture-paper-warm.png',
  toolsFlatlay: '/illustrations/tools-flatlay.png',
  hudsonConsultation: '/illustrations/hudson-consultation.png',
  hudsonInspecting: '/illustrations/hudson-inspecting.png',
  hudsonAtDoor: '/illustrations/hudson-at-door.png',
  portfolioHandoff: '/illustrations/portfolio-handoff.png',
  clipboardChecklist: '/illustrations/clipboard-checklist.png',
  houseCrosssection: '/illustrations/house-crosssection.png',
  houseExterior: '/illustrations/house-exterior.png',
  iconKey: '/illustrations/icon-key.png',
  myhomeScoreReport: '/illustrations/myhome-score-report.png',
  watchLuxury: '/illustrations/watch-luxury.png',
} as const

export const blendMask = (shape: string) => ({
  WebkitMaskImage: shape,
  maskImage: shape,
})

export const ScreenedBg: React.FC<{
  src: string
  blend?: string
  opacity?: number
  className?: string
  style?: React.CSSProperties
}> = ({ src, blend = 'multiply', opacity = 0.5, className = '', style }) => (
  <img
    src={src}
    alt=""
    aria-hidden
    className={`pointer-events-none select-none ${className}`}
    style={{ mixBlendMode: blend as React.CSSProperties['mixBlendMode'], opacity, ...style }}
  />
)

export const MarketingReveal: React.FC<{
  children: React.ReactNode
  delay?: number
  y?: number
  className?: string
}> = ({ children, delay = 0, y = 50, className = '' }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease, delay }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

/** Full-bleed accent strip between sections — pass different `accentSrc` per page */
export const SketchBreak: React.FC<{
  bg?: string
  accentSrc?: string
  blend?: string
  accentOpacity?: number
}> = ({
  bg = PAPER,
  accentSrc = ACCENT.sketchColonial,
  blend = 'multiply',
  accentOpacity = 0.35,
}) => {
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], ['5%', '-5%'])
  return (
    <section ref={ref} className="relative overflow-hidden py-4 md:py-5" style={{ backgroundColor: bg }}>
      <motion.div className="relative w-full max-h-[min(220px,28vw)] mx-auto flex justify-center" style={{ y }}>
        <ScreenedBg
          src={accentSrc}
          blend={blend ?? 'multiply'}
          opacity={accentOpacity}
          className="w-full max-w-[1400px] h-auto object-contain object-center"
          style={blendMask('linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)')}
        />
      </motion.div>
    </section>
  )
}

export const MKEyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <p
    className={`font-sans text-xs sm:text-sm font-semibold uppercase tracking-[0.28em] mb-5 lg:mb-6 ${className}`}
    style={{ color: marketingBrand.sandrift }}
  >
    {children}
  </p>
)

export const MKRule: React.FC<{ className?: string; wide?: boolean }> = ({ className = '', wide }) => (
  <div
    className={`h-px mb-6 lg:mb-8 origin-left ${wide ? 'w-20 lg:w-36' : 'w-14'} ${className}`}
    style={{ backgroundColor: marketingBrand.sandrift }}
    aria-hidden
  />
)

export const MKH2: React.FC<{ children: React.ReactNode; className?: string; light?: boolean }> = ({
  children,
  className = '',
  light,
}) => (
  <h2
    className={`font-serif font-normal ${className}`}
    style={{
      fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
      lineHeight: 1.08,
      letterSpacing: '-0.03em',
      color: light ? marketingBrand.white : marketingBrand.tapeTurquoise,
    }}
  >
    {children}
  </h2>
)

export const MKBody: React.FC<{ children: React.ReactNode; className?: string; light?: boolean }> = ({
  children,
  className = '',
  light,
}) => (
  <p
    className={`font-sans text-base lg:text-lg leading-[1.85] ${className}`}
    style={{ color: light ? 'rgba(255,255,255,0.78)' : colors.drillbitSkies }}
  >
    {children}
  </p>
)

/** Subtle paper noise (homepage final CTA) */
export const PaperTextureOverlay: React.FC<{ opacity?: number }> = ({ opacity = 0.04 }) => (
  <div
    className="pointer-events-none absolute inset-0"
    style={{
      opacity,
      backgroundImage: `url(${ACCENT.textureWarm})`,
      backgroundSize: '480px',
      mixBlendMode: 'multiply',
    }}
    aria-hidden
  />
)

/** Watermark emboss behind hero copy — desktop only */
export const HeroEmboss: React.FC<{ variant?: 'colonial' | 'modern' | 'estate' }> = ({ variant = 'colonial' }) => {
  const src =
    variant === 'modern' ? ACCENT.embossModern : variant === 'estate' ? ACCENT.embossEstate : ACCENT.embossColonial
  return (
    <div className="absolute inset-0 hidden lg:block pointer-events-none z-[1]" aria-hidden>
      <div className="absolute top-1/2 left-[8%] xl:left-[12%] -translate-y-[42%] w-[min(520px,42vw)] xl:w-[600px] opacity-[0.07]">
        <ScreenedBg
          src={src}
          blend="multiply"
          opacity={1}
          className="w-full h-auto"
          style={blendMask('radial-gradient(ellipse 50% 50% at 50% 50%, black 10%, transparent 55%)')}
        />
      </div>
    </div>
  )
}

/** Launch offer announcement strip — sits below the fixed Navbar */
export const HudsonOfferBanner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <section
    className={`relative overflow-hidden pt-20 lg:pt-24 ${className}`}
    style={{ backgroundColor: marketingBrand.tapeTurquoise }}
  >
    <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `url(${ACCENT.textureWarm})`, backgroundSize: '480px', mixBlendMode: 'screen' }} aria-hidden />
    <div
      className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-4 md:py-5 flex flex-col sm:flex-row items-center gap-3 sm:gap-5 text-center sm:text-left"
      style={{ borderTop: `1px solid rgba(255,255,255,0.08)` }}
    >
      <span
        className="shrink-0 inline-block font-sans text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-sm"
        style={{ backgroundColor: marketingBrand.sandrift, color: marketingBrand.tapeTurquoise }}
      >
        Launch Offer
      </span>
      <p className="font-serif text-[13px] sm:text-sm lg:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
        During our initial launch, the{' '}
        <span className="font-semibold text-white">MyHome Snapshot&#8482;</span>,{' '}
        <span className="font-semibold text-white">MyHome Score&#8482;</span>, and your personalized{' '}
        <span className="font-semibold text-white">MyHome Blueprint&#8482;</span>{' '}
        are provided at no cost&thinsp;&mdash;&thinsp;a{' '}
        <span className="font-semibold" style={{ color: marketingBrand.sandrift }}>$1,495 value</span>, included with membership.
      </p>
    </div>
  </section>
)

/** Framed photo block matching homepage cards */
export const MKImageFrame: React.FC<{
  src: string
  alt: string
  aspect?: string
  className?: string
}> = ({ src, alt, aspect = 'aspect-[4/5]', className = '' }) => (
  <div
    className={`overflow-hidden rounded-sm shadow-[0_24px_60px_-12px_rgba(13,49,53,0.18)] ring-1 ring-black/[0.06] ${aspect} ${className}`}
  >
    <img src={src} alt={alt} className="h-full w-full object-cover" loading="lazy" decoding="async" />
  </div>
)
